import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PORTFOLIO } from "@/constants/testIds";
import { SECTORS } from "@/data/sectors";

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Sector tile — large interactive card that links to /portfolio/[slug]
const SectorTile = ({ sector, idx }) => {
  const { name, code, slug: ssl, Icon, tagline, companies, image } = sector;
  const count = companies.length;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/portfolio/${ssl}`}
        data-testid={PORTFOLIO.card(slug(name))}
        className="group relative block h-full bg-[#FFFFFF]/70 backdrop-blur-sm border border-[#E0DDD6] hover:border-[#136207] transition-colors overflow-hidden min-h-[360px]"
      >
        {/* Image — full crispness, subtle zoom on hover */}
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.04]"
          style={{ backgroundImage: `url(${image})` }}
        />
        {/* Bottom text-legibility gradient (only where copy sits) */}
        <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#F7F6F3] via-[#F7F6F3]/90 to-transparent" />
        {/* Small top nib so overline + arrow read cleanly */}
        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#F7F6F3]/70 to-transparent" />

        {/* Hover glow */}
        <span
          aria-hidden
          className="absolute -bottom-20 -right-20 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(19,98,7,0.30) 0%, rgba(19,98,7,0) 70%)",
            filter: "blur(24px)",
          }}
        />

        <div className="relative h-full p-8 lg:p-10 flex flex-col">
          {/* Top */}
          <div className="flex items-start justify-between">
            <span className="overline">Sector · {code}</span>
            <ArrowUpRight className="w-5 h-5 text-[#8A8880] group-hover:text-[#136207] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
          </div>

          {/* Icon */}
          <div
            className="mt-10 w-16 h-16 border border-[#136207]/40 bg-[#FFFFFF]/60 backdrop-blur flex items-center justify-center"
            style={{ boxShadow: "inset 0 0 30px rgba(19,98,7,0.18)" }}
          >
            <div
              className="w-10 h-10 text-[#136207]"
            >
              <Icon className="w-full h-full" />
            </div>
          </div>

          {/* Name + tagline */}
          <div className="mt-8 flex-1">
            <h3 className="font-display text-[#1A1A1A] text-3xl sm:text-4xl tracking-tight leading-tight break-words">
              {name}
            </h3>
            <p className="mt-4 text-[#5C5A55] text-sm leading-relaxed font-light max-w-sm">
              {tagline}
            </p>
          </div>

          {/* Bottom */}
          <div className="mt-8 flex items-center justify-between border-t border-[#E0DDD6] pt-5">
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#136207]/90">
              {count > 0
                ? `${count} ${count === 1 ? "company" : "companies"}`
                : "More to be announced"}
            </span>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5A55] group-hover:text-[#1A1A1A] transition-colors">
              View <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const Portfolio = () => {
  return (
    <section
      id="portfolio"
      data-testid={PORTFOLIO.root}
      className="relative py-28 lg:py-40 border-t border-[#E0DDD6]"
    >
      {/* Section-local glow */}
      <div
        aria-hidden
        className="absolute top-1/3 left-[-10%] w-[55vw] h-[55vw] max-w-[800px] max-h-[800px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(19,98,7,0.07) 0%, rgba(19,98,7,0) 60%)",
          filter: "blur(50px)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="inline-block w-8 h-px bg-[#136207]" />
          <span className="overline text-[#136207]">02 · Portfolio</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-12 font-display text-[#1A1A1A] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium"
          >
            <span className="text-[#136207]">Strategic Capital</span> Sectors.
          </motion.h1>
        </div>

        {/* Sector gateway grid */}
        <div
          data-testid={PORTFOLIO.grid}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E0DDD6] border border-[#E0DDD6] backdrop-blur-sm"
        >
          {SECTORS.map((s, i) => (
            <SectorTile key={s.slug} sector={s} idx={i} />
          ))}
        </div>

        <p className="overline text-[#8A8880] mt-10">
          Click a sector to view the companies in detail · subject to update
        </p>
      </div>
    </section>
  );
};

export default Portfolio;
